import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Database, Activity, AlertCircle, X } from 'lucide-react';
import Papa from 'papaparse';
import { DataProcessingProgress } from './DataProcessingProgress';

interface DataManagementProps {
  onDataUpload: (data: any[]) => void;
  onStreamingConfigUpdate: (config: StreamingConfig) => void;
  setActiveSection?: (section: string) => void;
}

interface StreamingConfig {
  endpoint: string;
  interval: number;
  enabled: boolean;
}

export const DataManagement: React.FC<DataManagementProps> = ({
  onDataUpload,
  onStreamingConfigUpdate,
  setActiveSection
}) => {
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [streamingConfig, setStreamingConfig] = useState<StreamingConfig>({
    endpoint: '',
    interval: 1000,
    enabled: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onDrop = async (acceptedFiles: File[]) => {
    setError(null);
    const file = acceptedFiles[0];
    
    if (!file) return;

    try {
      if (file.name.endsWith('.csv')) {
        Papa.parse(file, {
          complete: (results) => {
            setParsedData(results.data);
            onDataUpload(results.data);
            setIsProcessing(true);
          },
          header: true,
          error: (error) => {
            setError(`Failed to parse CSV: ${error.message}`);
          },
        });
      } else if (file.name.endsWith('.xlsx')) {
        // Simulate Excel processing
        setParsedData([
          { timestamp: '2024-03-14 10:00:00', value: 23.5, componentId: 'valve-1' },
          { timestamp: '2024-03-14 10:00:01', value: 24.0, componentId: 'pump-1' },
        ]);
        setIsProcessing(true);
      }
    } catch (error) {
      setError(`Failed to process file: ${(error as Error).message}`);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  if (isProcessing) {
    return <DataProcessingProgress />;
  }

  return (
    <div className="space-y-6">
      {/* Historical Data Upload */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Database className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold">Historical Data</h2>
          </div>
        </div>

        <div
          {...getRootProps()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
        >
          <input {...getInputProps()} />
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            {isDragActive
              ? "Drop the files here..."
              : "Drag 'n' drop data files, or click to select"}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Supported formats: CSV, Excel (max 50MB)
          </p>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-grow">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 text-red-500 hover:text-red-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};